import {
    ISignatureApplet,
    IUnpackResult,
    IPackOptions,
    IPromise,
    ICrutchBlob,
    ICrutchFile,
    ICertificate,
    ISignOptions,
    IVersionInfo,
    ICryptoProfiles,
    IDecryptOptions,
    IEncryptOptions
} from './ISignatureApplet';
import { Deferred } from './internals/Deferred';
import { send, getOptions, toCrutchBlob } from './internals/helpers';

export class CryptoClient implements ISignatureApplet {
    private readonly settings: ICryptoClientOptions;
    private readonly baseUrl: string;
    private sessionPromise: IPromise<ICryptoSession>;

    static settings: ICryptoClientOptions = {
        defaultPort: 48737,
        port: { value: -1 },
        useFileApi: false,
        transformPromise: promise => promise,
        noSsl: false
    };

    /**
     * Текущая открытая сессия
     * @readonly
     */
    session: ICryptoSession = null;

    constructor(private options?: ICryptoClientOptions) {
        const { noSsl, port } = this.settings = getOptions(options, CryptoClient.settings);
        this.baseUrl = (noSsl ? 'http' : 'https') + '://127.0.0.1:';
    }

    /**
     * Возвращает информацию о версии криптосервиса
     */
    getVersion(): IPromise<IVersionInfo> {
        const promise = send<IVersionInfo>('GET', this.baseUrl, '/version', null);
        return this.settings.transformPromise(promise);
    }

    /**
    * Возвращает информацию о криптографических профилях
    */
    getProfiles(): IPromise<ICryptoProfiles> {
        const promise = send<ICryptoProfiles>('GET', this.baseUrl, '/csp/profiles', null);
        return this.settings.transformPromise(promise);
    }

    /**
     * Возвращает список установленных справочников сертификатов: 'GOST' и/или 'RSA'
     */
    getTypes(): IPromise<string[]> {
        const promise = send<string[]>('POST', this.baseUrl, '/csp/types', null);
        return this.settings.transformPromise<string[]>(promise);
    }

    /**
     * Открывает сессию. Пытается открыть существующую сессию, если указан {@type ICryptoSessionId индентификатор}.
     * Затем пытается открыть новую сессию, если указаны {@type ICryptoSessionParams параметры}.
     * @param args идентификатор и/или параметры сессии
     * @returns открытая сессия
     */
    open(args: ICryptoSessionId | ICryptoSessionParams): IPromise<ICryptoSession> {
        this.session = null;
        const promise = this.sessionPromise = send<ICryptoSession>('POST', this.baseUrl, '/csp/open', args, res => {
            return this.session = {
                id: res.id,
                type: res.type,
                profile: res.profile
            };
        });
        promise.then(null, () => {
            this.sessionPromise = null;
        });
        return this.settings.transformPromise<ICryptoSession>(promise);
    }

    /**
     * Закрывает сессию
     * @returns закрытая сессия
     */
    close(): IPromise<ICryptoSession> {
        return this.executeSession('POST', '/csp/close', null, res => {
            this.sessionPromise = null;
            this.session = null;
            return {
                id: res.id,
                type: res.type,
                profile: res.profile
            };
        });
    }

    pack(files: File[] | ICrutchFile[], options: IPackOptions): IPromise<Blob | ICrutchBlob> {
        const arr: ICrutchFile[] = new Array(files.length);
        const deferred = new Deferred<Blob | ICrutchBlob>();

        const sendRequest = () => {
            this.executeSession<Blob | ICrutchBlob>('POST', '/csp/pack', {
                files: arr,
                options: options
            }).then(x => deferred.resolve(x), x => deferred.reject(x));
        };

        const resolveFile = ((cur, len) => {
            return () => {
                if (++cur !== len) return;
                sendRequest();
            };
        })(0, files.length);

        let rejectFile = (err) => {
            rejectFile = () => { };
            deferred.reject(err);
        };

        (<any[]>files).forEach((file, index) => {
            toCrutchBlob(file).then(b => {
                arr[index] = {
                    name: file.name,
                    content: b.content,
                    encoding: b.encoding
                };
                resolveFile();
            }, rejectFile);
        });

        return this.settings.transformPromise<Blob | ICrutchBlob>(deferred);
    }

    unpack(buffer: Blob | ICrutchBlob): IPromise<IUnpackResult> {
        const deferred = new Deferred<IUnpackResult>();

        toCrutchBlob(buffer).then(b => {
            return this.executeSession<IUnpackResult>('POST', '/csp/unpack', {
                data: b
            }).then(x => deferred.resolve(x), x => deferred.reject(x));
        }, x => deferred.reject(x));

        return this.settings.transformPromise<IUnpackResult>(deferred);
    }

    getCerts(): IPromise<ICertificate[]> {
        return this.executeSession('POST', '/csp/certs', null);
    }

    sign(data: Blob | ICrutchBlob, options?: ISignOptions, signature?: Blob | ICrutchBlob): IPromise<Blob | ICrutchBlob> {
        const deferred = new Deferred<Blob | ICrutchBlob>();

        let detached: boolean;
        let signIn: ICrutchBlob;
        if (signature) {
            toCrutchBlob(signature).then(b => signIn = b);
            detached = true;
        }
        else {
            signIn = null;
            detached = false;
        }

        toCrutchBlob(data).then(b => {
            return this.executeSession<Blob | ICrutchBlob>('POST', '/csp/sign', {
                data: b,
                signature: signIn,
                options: getOptions(options, {
                    pkcs7: false,
                    detached: detached,
                    sendCertificate: false,
                    sendChain: false
                })
            }).then(x => deferred.resolve(x), x => deferred.reject(x));
        }, x => deferred.reject(x));

        return this.settings.transformPromise<Blob | ICrutchBlob>(deferred);
    }

    encrypt(data: Blob | ICrutchBlob, options: IEncryptOptions): IPromise<Blob | ICrutchBlob> {
        const deferred = new Deferred<Blob | ICrutchBlob>();

        toCrutchBlob(data).then(b => {
            return this.executeSession<Blob | ICrutchBlob>('POST', '/csp/encrypt', {
                data: b,
                options: getOptions(options, {
                    pkcs7: options.pkcs7,
                    flags: options.flags,
                    receivers: options.receivers
                })
            }).then(x => deferred.resolve(x), x => deferred.reject(x));
        }, x => deferred.reject(x));

        return this.settings.transformPromise<Blob | ICrutchBlob>(deferred);
    }

    decrypt(data: Blob | ICrutchBlob, options: IDecryptOptions): IPromise<Blob | ICrutchBlob> {
        const deferred = new Deferred<Blob | ICrutchBlob>();

        toCrutchBlob(data).then(b => {
            return this.executeSession<Blob | ICrutchBlob>('POST', '/csp/decrypt', {
                data: b,
                options: getOptions(options, {
                    pkcs7: options.pkcs7,
                    flags: options.flags
                })
            }).then(x => deferred.resolve(x), x => deferred.reject(x));
        }, x => deferred.reject(x));

        return this.settings.transformPromise<Blob | ICrutchBlob>(deferred);
    }

    importCertificate(data: Blob): IPromise<void> {
        const deferred = new Deferred<void>();

        toCrutchBlob(data).then(b => {
            return this.executeSession<void>('POST', '/csp/import', {
                data: b
            }).then(x => deferred.resolve(x), x => deferred.reject(x));
        }, x => deferred.reject(x));

        return this.settings.transformPromise(deferred);
    }

    getHeadCert(): IPromise<ICertificate> {
        return this.executeSession('POST', '/csp/head', null);
    }

    getHeadCertFor(args: ICryptoSessionParams): IPromise<ICertificate> {
        const deferred = new Deferred<ICertificate>();

        const cc = new CryptoClient(this.options);

        cc.open(args).then(
            () => {
                cc.getHeadCert().then(
                    (c) => {
                        cc.close().then(
                            () => deferred.resolve(c),
                            err => deferred.reject(err)
                        );
                    },
                    (err) => {
                        cc.close().then(
                            () => deferred.reject(err),
                            () => deferred.reject(err)
                        );
                    });
            },
            (err) => deferred.reject(err));

        return this.settings.transformPromise<ICertificate>(deferred);
    }

    private executeSession<T>(method: string, path: string, data: any, success?: (res: any) => T): IPromise<T> {
        return this.whenSession(() => {
            if (data == null) {
                data = this.session.id;
            } else {
                data.id = this.session.id;
            }
            return send(method, this.baseUrl, path, data, success);
        });
    }

    private whenSession<T>(func: () => IPromise<T>): IPromise<T> {
        const deferred = new Deferred<T>();

        if (this.sessionPromise) {
            this.sessionPromise.then(() => {
                func().then(x => deferred.resolve(x), x => deferred.reject(x), x => deferred.notify(x));
            }, noSession);
        } else {
            noSession();
        }

        function noSession() {
            deferred.reject({
                code: 'E_NO_SESSION',
                message: 'There is no active session'
            });
        }

        return this.settings.transformPromise<T>(deferred);
    }
}

export interface ICryptoClientOptions {
    /**
     * Порт коммутатора
     * @default 48737
     */
    defaultPort?: number;

    /**
     * Порт крипто-сервиса
     * @default 48737
     */
    port?: {value};

    /**
     * Использовать объекты File API: Blob, File - в результатах методов
     * @default false
     * @todo реализовать опцию
     */
    useFileApi?: boolean;

    /**
     * Функция преобразования объектов {@type IPromise}
     * @default identity функция, возвращающая первый параметр
     */
    transformPromise?: <T>(promise: IPromise<T>) => IPromise<T>;

    /**
     * Делать запросы к серверу по HTTP
     * @default false
     */
    noSsl?: boolean;
}

/**
 * Идентификатор сессии
 */
export interface ICryptoSessionId {
    /**
     * Идентификатор
     */
    id: string;
}

/**
 * Параметры сессии
 */
export interface ICryptoSessionParams {
    /**
     * Тип криптографии: 'GOST' или 'RSA'
     */
    type: string;

    /**
     * Профиль
     * @default 'My'
     */
    profile?: string;
}

/**
 * Сессия
 */
export interface ICryptoSession extends ICryptoSessionId {
    id: string;
    type: string;
    profile: string;
}
